import { useQuery } from '@tanstack/react-query';

import { PageHeader } from '@/components/layout/PageHeader';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CountryLabel } from '@/components/shared/CountryLabel';
import { StatusPill } from '@/components/shared/StatusPill';
import { getMyTenant, TENANT_ME_QUERY_KEY } from '@/lib/api/tenants';
import type { CarrierConnectionSummary } from '@/lib/api/carrier-connections';
import type { ShipFromAddress } from '@/lib/api/ship-from-addresses';
import { formatDate } from '@/lib/format';

/**
 * Carriers — the shipping carriers labels are generated with, plus the
 * ship-from address (a single tenant default in MVP; per-carrier
 * addresses are a future requirement, which is why ship-from lives
 * here rather than under Settings). Read-only summary; connect /
 * disconnect lands in a follow-up. Off the `tenant/me` query.
 */
export function CarriersPage() {
  const tenantQuery = useQuery({
    queryKey: TENANT_ME_QUERY_KEY,
    queryFn: async () => {
      const result = await getMyTenant();
      if (result.isErr()) throw new Error(result.error.message);
      return result.value;
    },
  });

  const tenant = tenantQuery.data;

  return (
    <div className="max-w-3xl">
      <PageHeader
        title="Carriers"
        subtitle="The shipping carriers your labels are generated with"
      />
      {tenant ? (
        <div className="space-y-6">
          <CarriersCard connections={tenant.carrier_connections} />
          <ShipFromCard address={tenant.default_ship_from_address} />
        </div>
      ) : null}
    </div>
  );
}

function CarriersCard({ connections }: { connections: readonly CarrierConnectionSummary[] }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Connected carriers</CardTitle>
        <CardDescription>Carriers available when generating labels.</CardDescription>
      </CardHeader>
      <CardContent>
        {connections.length > 0 ? (
          <ul className="divide-border divide-y">
            {connections.map((carrier) => (
              <li
                key={carrier.carrier_connection_id}
                className="flex items-center justify-between gap-4 py-3 first:pt-0 last:pb-0"
              >
                <div className="min-w-0">
                  <p className="text-foreground truncate text-sm font-medium">
                    {carrier.display_name}
                  </p>
                  <p className="text-muted-foreground text-xs">
                    Connected {formatDate(carrier.created_at)}
                  </p>
                </div>
                {carrier.status === 'active' ? (
                  <StatusPill tone="success">Active</StatusPill>
                ) : (
                  <StatusPill tone="muted">Revoked</StatusPill>
                )}
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-muted-foreground text-sm">No carriers connected.</p>
        )}
      </CardContent>
    </Card>
  );
}

function ShipFromCard({ address }: { address?: ShipFromAddress }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Ship-from address</CardTitle>
        <CardDescription>Where your parcels are sent from.</CardDescription>
      </CardHeader>
      <CardContent>
        {address ? (
          <address className="text-foreground text-sm not-italic leading-relaxed">
            {address.line1}
            <br />
            {address.line2 ? (
              <>
                {address.line2}
                <br />
              </>
            ) : null}
            {address.state ? `${address.city}, ${address.state}` : address.city}
            <br />
            <span className="tabular-nums">{address.postal_code}</span>
            <br />
            <CountryLabel code={address.country} />
          </address>
        ) : (
          <p className="text-muted-foreground text-sm">No ship-from address on file.</p>
        )}
      </CardContent>
    </Card>
  );
}
